/**
 * PartyManager - 파티(그룹) 상태 + 초대/수락/거절 흐름
 *
 * - 파티원 식별은 캐릭터 이름(전역 유일). 최대 8인, 파티장만 초대/강퇴.
 * - 초대 흐름: 초대 전송 -> 상대 화면에 "OOO이(가) 파티에 초대합니다" [수락]/[거절] -> 합류 또는 거절 통보
 * - 전송 계층은 PartyTransport로 분리 (현재: WebSocket 서버 중계 = NetPartyTransport).
 */

import { EntityRegistry } from '../../core/EntityRegistry';
import { Currency } from '../../core/Currency';

export const PARTY_MAX = 8;
const INVITE_TTL_MS = 30000;
const ONLINE_TTL_MS = 4000;
const MEMBER_TTL_MS = 9000;
const HEARTBEAT_SEC = 1;

export type PartyMsg =
  | { t: 'status'; name: string; job: string; level: number; hp: number }
  | { t: 'invite'; from: string; to: string }
  | { t: 'accept'; from: string; to: string }
  | { t: 'decline'; from: string; to: string; reason?: 'already' | 'busy' | 'expired' }
  | { t: 'sync'; leader: string; members: string[] }
  | { t: 'leave'; from: string }
  | { t: 'kick'; to: string }
  | { t: 'party_reward'; from: string; copper: number; fame: number; monsterName: string };

export interface PartyTransport {
  send(msg: PartyMsg): void;
  onMessage(cb: (msg: PartyMsg) => void): void;
}

export interface PartyMemberView {
  name: string;
  job: string;
  level: number;
  hp: number;
  leader: boolean;
  isMe: boolean;
}

export interface PartyResult {
  ok: boolean;
  message: string;
}

interface Seen {
  job: string;
  level: number;
  hp: number;
  at: number;
}

export class PartyManager {
  private transport: PartyTransport;
  private members: string[] = [];
  private leader: string | null = null;
  private seen = new Map<string, Seen>();
  private incoming: { from: string; expires: number } | null = null;
  private outgoing = new Map<string, number>();
  private hb = 0;

  /** 초대를 받았을 때 (UI에서 수락/거절 팝업 표시) */
  public onIncomingInvite?: (from: string) => void;
  /** 받은 초대가 처리/만료되어 팝업을 닫아야 할 때 */
  public onInviteClosed?: () => void;
  /** 채팅창 등에 보여줄 알림 */
  public onNotice?: (text: string) => void;

  constructor(private registry: EntityRegistry, transport: PartyTransport) {
    this.transport = transport;
    this.transport.onMessage((m) => this.handle(m));
  }

  private isRemoteOnline(name: string): boolean {
    const p = this.registry.getPlayer();
    if (!p) return false;
    // EntityRegistry에 존재하는 원격 플레이어 또는 동료 확인
    return this.registry.getAll().some(
      (e) => e.name === name && (e.category === 'player' || e.category === 'companion') && !e.isDead
    );
  }

  private myName(): string | null {
    return this.registry.getPlayer()?.name ?? null;
  }

  private notice(text: string): void {
    this.onNotice?.(text);
  }

  public isInParty(): boolean {
    return this.members.length > 1;
  }

  public getMembers(): string[] {
    return this.members.slice();
  }

  public isLeader(): boolean {
    return this.isInParty() && this.leader === this.myName();
  }

  public getLeaderName(): string | null {
    return this.leader;
  }

  public hasIncomingInvite(): string | null {
    return this.incoming ? this.incoming.from : null;
  }

  /** 파티가 아니면 본인만, 파티면 전체 파티원 */
  public getMemberViews(): PartyMemberView[] {
    const me = this.registry.getPlayer();
    if (!me) return [];
    const view = (name: string): PartyMemberView => {
      if (name === me.name) {
        const hp = Math.round((me.resources.hp / Math.max(1, me.resources.maxHp)) * 100);
        return { name, job: me.job || '', level: me.level ?? 1, hp, leader: this.leader === name, isMe: true };
      }
      const s = this.seen.get(name);
      return { name, job: s?.job || '', level: s?.level ?? 1, hp: s?.hp ?? 0, leader: this.leader === name, isMe: false };
    };
    return this.isInParty() ? this.members.map(view) : [view(me.name)];
  }

  /** 현재 접속 중(최근 신호가 있거나 같은 존에 보이는) 다른 캐릭터 이름 */
  public getOnlineNames(): string[] {
    const now = performance.now();
    const me = this.myName();
    const seenNames = [...this.seen.entries()]
      .filter(([n, s]) => n !== me && now - s.at < ONLINE_TTL_MS)
      .map(([n]) => n);

    // EntityRegistry에 존재하는 원격 접속자나 동료도 포함
    const remoteNames = this.registry
      .getAll()
      .filter((e) => e.name !== me && (e.category === 'player' || e.category === 'companion') && !e.isDead)
      .map((e) => e.name);

    return Array.from(new Set([...seenNames, ...remoteNames])).sort();
  }

  private isOnline(name: string): boolean {
    const s = this.seen.get(name);
    if (s && performance.now() - s.at < ONLINE_TTL_MS) return true;
    return this.isRemoteOnline(name);
  }

  // ------------------------------------------------------------------ 행동

  public invite(toName: string): PartyResult {
    const me = this.myName();
    const to = toName.trim();
    if (!me) return { ok: false, message: '캐릭터 정보를 찾을 수 없습니다.' };
    if (!to) return { ok: false, message: '초대할 캐릭터 이름을 입력하거나 대상을 선택해 주세요.' };
    if (to === me) return { ok: false, message: '자기 자신은 초대할 수 없습니다.' };
    if (this.members.includes(to)) return { ok: false, message: `[${to}]은(는) 이미 파티원입니다.` };
    if (this.isInParty() && !this.isLeader()) return { ok: false, message: '파티장만 초대할 수 있습니다.' };
    if (this.members.length >= PARTY_MAX) return { ok: false, message: `파티는 최대 ${PARTY_MAX}명입니다.` };
    if (!this.isOnline(to)) return { ok: false, message: `접속 중인 [${to}] 님을 찾을 수 없습니다.` };
    const pending = this.outgoing.get(to);
    if (pending && performance.now() < pending) return { ok: false, message: `[${to}] 님의 응답을 기다리는 중입니다.` };

    this.outgoing.set(to, performance.now() + INVITE_TTL_MS);
    this.transport.send({ t: 'invite', from: me, to });
    return { ok: true, message: `[${to}] 님에게 파티 초대를 보냈습니다. 응답을 기다립니다.` };
  }

  /** 받은 초대에 응답 */
  public respondInvite(accept: boolean): void {
    const me = this.myName();
    if (!this.incoming || !me) return;
    const from = this.incoming.from;
    this.incoming = null;
    this.onInviteClosed?.();
    if (accept) {
      this.transport.send({ t: 'accept', from: me, to: from });
      this.notice(`[${from}] 님의 파티 초대를 수락했습니다.`);
    } else {
      this.transport.send({ t: 'decline', from: me, to: from });
      this.notice(`[${from}] 님의 파티 초대를 거절했습니다.`);
    }
  }

  public leave(): PartyResult {
    const me = this.myName();
    if (!me || !this.isInParty()) return { ok: false, message: '가입한 파티가 없습니다.' };
    if (this.leader === me) {
      const rest = this.members.filter((n) => n !== me);
      this.transport.send({ t: 'sync', leader: rest[0], members: rest });
    } else {
      this.transport.send({ t: 'leave', from: me });
    }
    this.disband();
    return { ok: true, message: '파티에서 탈퇴했습니다.' };
  }

  public kick(name: string): PartyResult {
    if (!this.isLeader()) return { ok: false, message: '파티장만 강퇴할 수 있습니다.' };
    if (name === this.myName()) return { ok: false, message: '자기 자신은 강퇴할 수 없습니다. 탈퇴를 이용하세요.' };
    this.members = this.members.filter((n) => n !== name);
    this.transport.send({ t: 'kick', to: name });
    this.afterMembersChanged();
    return { ok: true, message: `[${name}] 님을 파티에서 강퇴했습니다.` };
  }

  /** 파티원들에게 사냥 보상(골드/명성) 공유 전송 - 돈은 자동 균등분배 */
  public broadcastReward(totalCopper: number, fame: number, monsterName: string): void {
    const me = this.myName();
    if (!me || !this.isInParty()) return;
    const count = Math.max(1, this.members.length);
    const splitCopper = Math.max(1, Math.floor(totalCopper / count));
    this.transport.send({
      t: 'party_reward',
      from: me,
      copper: splitCopper,
      fame,
      monsterName,
    });
  }

  // ------------------------------------------------------------------ 내부

  private disband(): void {
    this.members = [];
    this.leader = null;
  }

  private afterMembersChanged(): void {
    if (this.members.length <= 1) {
      this.disband();
      return;
    }
    this.transport.send({ t: 'sync', leader: this.leader!, members: this.members });
  }

  private handle(m: PartyMsg): void {
    const me = this.myName();
    if (!me) return;
    switch (m.t) {
      case 'status':
        if (m.name !== me) this.seen.set(m.name, { job: m.job, level: m.level, hp: m.hp, at: performance.now() });
        break;
      case 'invite':
        if (m.to !== me) break;
        if (this.isInParty()) {
          this.transport.send({ t: 'decline', from: me, to: m.from, reason: 'already' });
        } else if (this.incoming && performance.now() < this.incoming.expires) {
          this.transport.send({ t: 'decline', from: me, to: m.from, reason: 'busy' });
        } else {
          this.incoming = { from: m.from, expires: performance.now() + INVITE_TTL_MS };
          this.onIncomingInvite?.(m.from);
        }
        break;
      case 'accept': {
        if (m.to !== me) break;
        const until = this.outgoing.get(m.from);
        this.outgoing.delete(m.from);
        if (!until || performance.now() > until) break;
        if (this.members.length >= PARTY_MAX) break;
        if (!this.isInParty()) {
          this.members = [me];
          this.leader = me;
        }
        if (this.leader !== me) break;
        if (!this.members.includes(m.from)) this.members.push(m.from);
        this.transport.send({ t: 'sync', leader: this.leader, members: this.members });
        this.notice(`[${m.from}] 님이 파티에 합류했습니다.`);
        break;
      }
      case 'decline': {
        if (m.to !== me) break;
        this.outgoing.delete(m.from);
        const why =
          m.reason === 'already'
            ? '이미 다른 파티에 속해 있습니다.'
            : m.reason === 'busy'
            ? '다른 초대에 응답 중입니다.'
            : m.reason === 'expired'
            ? '초대에 응답하지 않았습니다.'
            : '초대를 거절했습니다.';
        this.notice(`[${m.from}] 님이 ${why}`);
        break;
      }
      case 'sync':
        if (m.members.includes(me)) {
          const wasIn = this.isInParty();
          this.members = m.members.slice();
          this.leader = m.leader;
          if (this.members.length <= 1) this.disband();
          else if (!wasIn) this.notice(`[${m.leader}] 님의 파티에 합류했습니다.`);
        } else if (this.isInParty() && this.leader === m.leader) {
          this.disband();
          this.notice('파티에서 제외되었습니다.');
        }
        break;
      case 'leave':
        if (this.isLeader() && this.members.includes(m.from)) {
          this.members = this.members.filter((n) => n !== m.from);
          this.notice(`[${m.from}] 님이 파티에서 탈퇴했습니다.`);
          this.afterMembersChanged();
        }
        break;
      case 'kick':
        if (m.to === me && this.isInParty()) {
          this.disband();
          this.notice('파티에서 강퇴되었습니다.');
        }
        break;
      case 'party_reward':
        if (this.isInParty() && this.members.includes(m.from) && m.from !== me) {
          const player = this.registry.getPlayer();
          if (player) {
            player.gold = (player.gold ?? 0) + m.copper;
            player.fame = (player.fame ?? 120) + m.fame;
            this.notice(`💰 [파티 균등 분배] [${m.from}] 님이 [${m.monsterName}] 처치! (개인당 명성 +${m.fame}, 분배금 +${Currency.format(m.copper)} 획득)`);
          }
        }
        break;
    }
  }

  /** 매 프레임 호출: 내 상태 신호, 만료 처리, 접속이 끊긴 파티원 정리 */
  public update(dt: number): void {
    const me = this.registry.getPlayer();
    if (!me) return;
    const now = performance.now();

    this.hb += dt;
    if (this.hb >= HEARTBEAT_SEC) {
      this.hb = 0;
      this.transport.send({
        t: 'status',
        name: me.name,
        job: me.job || '',
        level: me.level ?? 1,
        hp: Math.round((me.resources.hp / Math.max(1, me.resources.maxHp)) * 100),
      });
    }

    if (this.incoming && now > this.incoming.expires) {
      this.transport.send({ t: 'decline', from: me.name, to: this.incoming.from, reason: 'expired' });
      this.incoming = null;
      this.onInviteClosed?.();
    }

    for (const [n, until] of this.outgoing) if (now > until) this.outgoing.delete(n);

    if (this.isLeader()) {
      const gone = this.members.filter((n) => n !== me.name && !(this.seen.get(n) && now - this.seen.get(n)!.at < MEMBER_TTL_MS));
      if (gone.length) {
        this.members = this.members.filter((n) => !gone.includes(n));
        gone.forEach((n) => this.notice(`[${n}] 님의 접속이 끊겨 파티에서 제외되었습니다.`));
        this.afterMembersChanged();
      }
    }
  }
}
